import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckboxLine, FormField, FormGrid, HpButton, HpCardHeader, Icon, IconButton, TableScroll } from '@hp/ui';
import { UNIT_AVAILABILITIES, UnitInputSchema, type UnitAvailability, type UnitInput } from '@hp/domain';
import { api } from '../../api/client';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { toNumOrNull } from './field';
import { ValidationErrors } from './validation-errors';
import { zodIssueList } from './validation';
import { AvailabilityBadge } from './availability-badge';
import { useAvailabilityLabel } from './availability-labels';
import { invalidateProjects, projectFileUrl, type ProjectUnit } from './queries';

// Czech business amounts: CZK is formatted the Czech way in both UI languages.
const czk = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 });

/** Fields of UnitInput, as validation issues name them (`units.fields.*`). */
const UNIT_FIELDS = [
  'title',
  'description',
  'disposition',
  'price',
  'availability',
  'hasTerraceBalcony',
  'hasParking',
] as const;

export function UnitsSection({ projectId, units }: { projectId: string; units: ProjectUnit[] }) {
  const { t } = useTranslation(['projects', 'common']);
  /** null = closed, 'new' = creating, otherwise the unit being edited. */
  const [editing, setEditing] = useState<ProjectUnit | 'new' | null>(null);
  const canEdit = useCan()('projects.edit');

  const brochureInput = useRef<HTMLInputElement>(null);
  const brochureUnitId = useRef<string | null>(null);

  const uploadBrochure = useMutation({
    mutationFn: ({ unitId, file }: { unitId: string; file: File }) => {
      const form = new FormData();
      form.append('brochure', file);
      return api.upload<{ fileId: string }>(`/api/projects/${projectId}/units/${unitId}/brochure`, form);
    },
    onSuccess: invalidateProjects,
  });

  const removeBrochure = useMutation(
    trpc.projects.units.removeBrochure.mutationOptions({ onSuccess: invalidateProjects }),
  );
  const removeUnit = useMutation(trpc.projects.units.remove.mutationOptions({ onSuccess: invalidateProjects }));

  const check = (v: boolean) =>
    v ? <Icon name="check" className="bo-check" aria-label={t('units.yes')} /> : <span className="bo-muted">—</span>;

  return (
    <section className="hp-card">
      <HpCardHeader title={t('units.title')} subtitle={t('units.subtitle', { count: units.length })} icon="home">
        {canEdit && (
          <HpButton variant="primary" icon="plus" onClick={() => setEditing('new')}>
            {t('units.add')}
          </HpButton>
        )}
      </HpCardHeader>

      <ErrorAlert
        error={uploadBrochure.error ?? removeBrochure.error ?? removeUnit.error}
        className="bo-alert--inset"
      />

      <input
        ref={brochureInput}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          const unitId = brochureUnitId.current;
          if (file && unitId !== null) uploadBrochure.mutate({ unitId, file });
          e.target.value = '';
        }}
      />

      <TableScroll label={t('units.tableLabel')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('units.columns.title')}</th>
            <th>{t('units.columns.disposition')}</th>
            <th className="bo-cell-num">{t('units.columns.price')}</th>
            <th>{t('units.columns.availability')}</th>
            <th className="bo-cell-center">{t('units.columns.terraceBalcony')}</th>
            <th className="bo-cell-center">{t('units.columns.parking')}</th>
            <th>{t('units.columns.brochure')}</th>
            <th aria-label={t('common:table.actions')} />
          </BrutTheadRow>
          <tbody>
            {units.length === 0 && (
              <tr>
                <td colSpan={8} className="bo-cell-empty">
                  {t('units.empty')}
                </td>
              </tr>
            )}
            {units.map((unit) => (
              <tr key={unit.id}>
                <td className="bo-strong">{unit.title}</td>
                <td>{unit.disposition}</td>
                <td className="bo-cell-num">{czk.format(unit.price)}</td>
                <td>
                  <AvailabilityBadge availability={unit.availability} />
                </td>
                <td className="bo-cell-center">{check(unit.hasTerraceBalcony)}</td>
                <td className="bo-cell-center">{check(unit.hasParking)}</td>
                <td>
                  <span className="bo-row">
                    {unit.brochureFileId !== null && (
                      <a
                        href={projectFileUrl(unit.brochureFileId)}
                        target="_blank"
                        rel="noreferrer"
                        className="hp-detail-action hp-detail-action--text"
                        title={unit.brochureFilename ?? undefined}
                      >
                        {t('units.pdf')}
                      </a>
                    )}
                    <HpButton
                      className="bo-button--sm"
                      icon="upload"
                      busy={uploadBrochure.isPending && uploadBrochure.variables?.unitId === unit.id}
                      disabled={uploadBrochure.isPending || !canEdit}
                      onClick={() => {
                        brochureUnitId.current = unit.id;
                        brochureInput.current?.click();
                      }}
                    >
                      {unit.brochureFileId !== null ? t('units.replace') : t('units.upload')}
                    </HpButton>
                    {unit.brochureFileId !== null && (
                      <IconButton
                        icon="close"
                        aria-label={t('units.removeBrochure')}
                        title={t('units.removeBrochure')}
                        busy={removeBrochure.isPending && removeBrochure.variables?.unitId === unit.id}
                        disabled={removeBrochure.isPending || !canEdit}
                        onClick={() => {
                          if (window.confirm(t('units.confirmRemoveBrochure')))
                            removeBrochure.mutate({ projectId, unitId: unit.id });
                        }}
                      />
                    )}
                  </span>
                </td>
                <td className="bo-cell-actions">
                  <span className="bo-row bo-row--end">
                    <HpButton
                      className="bo-button--sm"
                      icon="edit"
                      disabled={!canEdit}
                      onClick={() => setEditing(unit)}
                    >
                      {t('common:actions.edit')}
                    </HpButton>
                    <HpButton
                      className="bo-button--sm bo-danger"
                      icon="trash"
                      busy={removeUnit.isPending && removeUnit.variables?.unitId === unit.id}
                      disabled={removeUnit.isPending || !canEdit}
                      onClick={() => {
                        if (window.confirm(t('units.confirmDelete', { title: unit.title })))
                          removeUnit.mutate({ projectId, unitId: unit.id });
                      }}
                    >
                      {t('common:actions.delete')}
                    </HpButton>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </BrutTable>
      </TableScroll>

      {editing !== null && (
        <UnitFormPanel
          key={editing === 'new' ? 'new' : editing.id}
          projectId={projectId}
          unit={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function UnitFormPanel({
  projectId,
  unit,
  onClose,
}: {
  projectId: string;
  unit: ProjectUnit | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['projects', 'common']);
  const availabilityLabel = useAvailabilityLabel();
  const [title, setTitle] = useState(unit?.title ?? '');
  const [disposition, setDisposition] = useState(unit?.disposition ?? '');
  const [price, setPrice] = useState(unit === null ? '' : String(unit.price));
  const [description, setDescription] = useState(unit?.description ?? '');
  const [availability, setAvailability] = useState<UnitAvailability>(unit?.availability ?? 'available');
  const [terraceBalcony, setTerraceBalcony] = useState(unit?.hasTerraceBalcony ?? false);
  const [parking, setParking] = useState(unit?.hasParking ?? false);
  const [issues, setIssues] = useState<string[]>([]);

  const onSaved = () => {
    void invalidateProjects();
    onClose();
  };
  const create = useMutation(trpc.projects.units.create.mutationOptions({ onSuccess: onSaved }));
  const update = useMutation(trpc.projects.units.update.mutationOptions({ onSuccess: onSaved }));
  const save = { isPending: create.isPending || update.isPending, error: create.error ?? update.error };

  const priceNum = toNumOrNull(price);
  const canSave = title.trim() !== '' && disposition.trim() !== '' && priceNum !== null && priceNum >= 0;

  function submit() {
    if (priceNum === null) return;
    const parsed = UnitInputSchema.safeParse({
      title: title.trim(),
      disposition: disposition.trim(),
      price: priceNum,
      availability,
      description: description.trim() === '' ? null : description,
      hasTerraceBalcony: terraceBalcony,
      hasParking: parking,
    } satisfies UnitInput);
    if (!parsed.success) {
      const labels = Object.fromEntries(UNIT_FIELDS.map((field) => [field, t(`units.fields.${field}`)]));
      setIssues(zodIssueList(parsed.error, labels));
      return;
    }
    setIssues([]);
    if (unit === null) create.mutate({ projectId, unit: parsed.data });
    else update.mutate({ projectId, unitId: unit.id, unit: parsed.data });
  }

  return (
    <div className="bo-subform">
      <h3 className="bo-subform__title">
        {unit === null ? t('units.form.new') : t('units.form.edit', { title: unit.title })}
      </h3>
      <FormGrid className="bo-form-grid--4">
        <FormField label={t('units.fields.title')} className="bo-span-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormField>
        <FormField label={t('units.fields.disposition')}>
          <input
            placeholder={t('units.form.dispositionPlaceholder')}
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
          />
        </FormField>
        <FormField label={t('units.form.priceCzk')}>
          <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
        </FormField>
        <FormField label={t('units.fields.availability')}>
          <select value={availability} onChange={(e) => setAvailability(e.target.value as UnitAvailability)}>
            {UNIT_AVAILABILITIES.map((a) => (
              <option key={a} value={a}>
                {availabilityLabel(a)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label={t('units.form.descriptionMarkdown')} className="bo-span-3">
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormField>
      </FormGrid>
      <div className="bo-row">
        <CheckboxLine checked={terraceBalcony} onChange={(e) => setTerraceBalcony(e.target.checked)}>
          {t('units.fields.hasTerraceBalcony')}
        </CheckboxLine>
        <CheckboxLine checked={parking} onChange={(e) => setParking(e.target.checked)}>
          {t('units.fields.hasParking')}
        </CheckboxLine>
      </div>
      <div className="bo-row">
        <HpButton variant="primary" icon="check" busy={save.isPending} disabled={!canSave} onClick={submit}>
          {save.isPending ? t('units.form.saving') : unit === null ? t('units.form.add') : t('units.form.save')}
        </HpButton>
        <HpButton onClick={onClose}>{t('common:actions.cancel')}</HpButton>
      </div>
      <ValidationErrors issues={issues} />
      <ErrorAlert error={save.error} />
    </div>
  );
}
